import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IonText, IonSpinner, IonButton, IonIcon } from "@ionic/react";
import { calendarOutline } from "ionicons/icons";
import { useHistory, useLocation } from "react-router";
import moment from "moment";
import MisAsignacionesService, {
  MisAsignacionesResponse,
} from "src/services/misAsignaciones";
import {
  Caso,
  CasoCard,
  CasoDetalleModal,
  DIA_KEYS,
  GARNET,
  emptyData,
  filtrarCasosPorFecha,
} from "src/components/miDia/misCasos";

const MiDiaCard: React.FC = () => {
  const [data, setData] = useState<MisAsignacionesResponse>(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [casoDetalle, setCasoDetalle] = useState<Caso | null>(null);
  const history = useHistory();
  const location = useLocation();

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await MisAsignacionesService.listar();
      setData({ ...emptyData, ...response });
    } catch (err) {
      console.error("Error al cargar Mi Dia:", err);
      setError("No se pudo cargar tu dia.");
      setData(emptyData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Deep-link desde la notificacion push: /home?caso=<confirmacion_id>.
  // Abre directamente el detalle de ese reemplazo (aunque no sea de hoy).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const caso = params.get("caso");
    if (!caso) return;
    const reemplazo = (data.reemplazos || []).find(
      (r: any) => String(r.confirmacion_id) === String(caso)
    );
    if (reemplazo) {
      setCasoDetalle({ tipo: "reemplazo", item: reemplazo });
      history.replace(location.pathname);
    }
  }, [location.search, location.pathname, data.reemplazos, history]);

  const keyHoy = DIA_KEYS[moment().day()];
  const casosHoy = useMemo<Caso[]>(
    () => filtrarCasosPorFecha(data, moment()),
    [data]
  );

  const onEstadoCambiado = useCallback(
    (confirmacionId: number, estado: string) => {
      setData((prev) => ({
        ...prev,
        reemplazos: prev.reemplazos.map((r: any) =>
          r.confirmacion_id === confirmacionId
            ? { ...r, estado_confirmacion: estado }
            : r
        ),
      }));
      setCasoDetalle((prev) =>
        prev && prev.item?.confirmacion_id === confirmacionId
          ? { ...prev, item: { ...prev.item, estado_confirmacion: estado } }
          : prev
      );
    },
    []
  );

  return (
    <div className="mt-1">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <IonIcon icon={calendarOutline} style={{ fontSize: 22, color: GARNET }} />
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>Mi dia</h2>
      </div>
      <div style={{ marginBottom: 10 }}>
        <IonText color="medium">
          <small style={{ textTransform: "capitalize" }}>
            {moment().locale("es-mx").format("dddd DD/MM/YYYY")}
          </small>
        </IonText>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
          <IonSpinner />
        </div>
      ) : error ? (
        <IonText color="danger">{error}</IonText>
      ) : casosHoy.length === 0 ? (
        <IonText color="medium">No tenes casos para hoy.</IonText>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {casosHoy.map((caso, idx) => (
            <CasoCard
              key={`${caso.tipo}-${idx}`}
              caso={caso}
              keyDia={keyHoy}
              onClick={() => setCasoDetalle(caso)}
            />
          ))}
        </div>
      )}

      {!loading && !error ? (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <IonButton
            fill="clear"
            size="small"
            onClick={() => history.push("/mis-asignaciones")}
          >
            Ver todo
          </IonButton>
        </div>
      ) : null}

      <CasoDetalleModal
        caso={casoDetalle}
        keyDia={keyHoy}
        esHoy={true}
        diaLabel="Hoy"
        onClose={() => setCasoDetalle(null)}
        onEstadoCambiado={onEstadoCambiado}
      />
    </div>
  );
};

export default MiDiaCard;
